import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';

import axiosInstance from '../axiosConfig';
import { clearComponentModalDataCache, getComponentModalData } from '../utils/componentModalDataCache';
import { AuthContext } from '../AuthContext';
import ProjectModal from './ProjectModal';
import UpgradeModal from './UpgradeModal';
import StorageCategoryModal from './StorageCategoryModal';
import CompatibilityModal from './CompatibilityModal';
import './EntityModals.css';

function ComponentModal({ show, handleClose, onSubmit }) {
    const [projects, setProjects] = useState([]);
    const [upgrades, setUpgrades] = useState([]);
    const [storages, setStorages] = useState([]);
    const [compatibilities, setCompatibilities] = useState([]);
    const { authTokens, selectedServiceCenter } = useContext(AuthContext);


    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showStorageCategoryModal, setShowStorageCategoryModal] = useState(false);
    const [showCompatibilityModal, setShowCompatibilityModal] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        photo: null,
        project: null,
        upgrade: null,
        storage_location: null,
        compatibility: [], // Массив для поддержки множественного выбора
        quantity_in_upgrade: '',
        batch_number: '',
        quantity: ''
    });

    const [errors, setErrors] = useState({});

    const normalizeFilters = useCallback((raw) => ({
        project: raw.project || '',
        upgrade: raw.upgrade || '',
        storage_location: raw.storage_location || '',
        compatibility: Array.isArray(raw.compatibility) ? raw.compatibility : [],
    }), []);

    // Мемозируем функцию fetchFilteredData с помощью useCallback
    const fetchFilteredData = useCallback(async (filters) => {
        try {
            const responseData = await getComponentModalData({
                authToken: authTokens.access,
                serviceCenterId: selectedServiceCenter?.id,
                filters: normalizeFilters(filters),
            });
            setProjects((responseData.projects || []).map(project => ({
                value: project.id,
                label: project.name
            })));
            setUpgrades((responseData.upgrades || []).map(upgrade => ({
                value: upgrade.id,
                label: upgrade.name
            })));
            setStorages((responseData.storages || []).map(storage => ({
                value: storage.id,
                label: storage.name
            })));
            setCompatibilities((responseData.compatibilities || []).map(compatibility => ({
                value: compatibility.id,
                label: compatibility.name
            })));
        } catch (error) {
            console.error('Ошибка при загрузке данных:', error);
            window.Toastify({
                text: "Ошибка при загрузке данных.",
                backgroundColor: "linear-gradient(to right, #ff5f6d, #ffc371)",
                className: "error-toast",
            }).showToast();
        }
    }, [authTokens.access, normalizeFilters, selectedServiceCenter?.id]); // Указываем зависимость от authTokens.access

    useEffect(() => {
        if (show && selectedServiceCenter?.id) {
            fetchFilteredData({
                project: null,
                upgrade: null,
                storage_location: null,
                compatibility: [],
            });
        }
    }, [show, fetchFilteredData, selectedServiceCenter?.id]);

    const handleFilterChange = (selectedOptions, { name }) => {
        let value;
        if (name === 'compatibility') {
            value = Array.isArray(selectedOptions)
                ? selectedOptions.map((option) => option.value)
                : [];
        } else {
            value = selectedOptions?.value || null;
        }

        const updatedFormData = {
            ...formData,
            [name]: value
        };
        setFormData(updatedFormData);
        fetchFilteredData(updatedFormData);  // Обновляем фильтры при изменении значений
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        setFormData({
            ...formData,
            [name]: e.target.files[0], // Прямое добавление файла
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Название не может быть пустым';
        if (!formData.description.trim()) newErrors.description = 'Описание не может быть пустым';
        if (!formData.photo) newErrors.photo = 'Фото должно быть загружено';
        if (!formData.project) newErrors.project = 'Необходимо выбрать проект';
        if (!formData.storage_location) newErrors.storage_location = 'Необходимо выбрать место хранения';
        if (formData.compatibility.length === 0) newErrors.compatibility = 'Необходимо выбрать хотя бы одну совместимость';
        if (formData.quantity_in_upgrade === '') newErrors.quantity_in_upgrade = 'Количество в апгрейде не может быть пустым';
        if (!formData.batch_number.trim()) newErrors.batch_number = 'Партийный номер не может быть пустым';
        if (formData.quantity === '') newErrors.quantity = 'Количество на складе не может быть пустым';

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const data = new FormData();
        for (const key in formData) {
            if (Array.isArray(formData[key])) {
                formData[key].forEach(item => data.append(key, item)); // Добавляем каждый элемент массива в formData
            } else if (formData[key] instanceof File) {
                data.append(key, formData[key]); // Прямое добавление файла
            } else {
                data.append(key, formData[key]);
            }
        }

        axiosInstance.post('/api/create_component/', data, {
            headers: {
                'Authorization': `Bearer ${authTokens.access}`,
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            if (response.data.success) {
                clearComponentModalDataCache(selectedServiceCenter?.id);
                onSubmit(response.data);
                handleClose();
                window.Toastify({
                    text: "Компонент успешно создан!",
                    backgroundColor: "linear-gradient(to right, #00b09b, #96c93d)",
                    className: "success-toast",
                }).showToast();
            } else {
                setErrors({ form: response.data.errors });
                window.Toastify({
                    text: response.data.errors || "Произошла ошибка при создании компонента.",
                    backgroundColor: "linear-gradient(to right, #ff5f6d, #ffc371)",
                    className: "error-toast",
                }).showToast();
            }
        })
        .catch(error => {
            window.Toastify({
                text: "Ошибка при создании компонента.",
                backgroundColor: "linear-gradient(to right, #ff5f6d, #ffc371)",
                className: "error-toast",
            }).showToast();
        });
    };

    // Обработчик для открытия страницы клонирования компонентов в Home.js
    const handleCloneClick = () => {
        onSubmit("clone");
        handleClose();
    };

    return (
        <>
            <Modal
                show={show}
                onHide={handleClose}
                centered
                className="entity-modal"
                dialogClassName="entity-modal-dialog entity-modal-dialog--xl"
                contentClassName="entity-modal-content"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Добавить компонент</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleFormSubmit} className="entity-form entity-form-grid">
                        {Object.values(errors).map((error, index) => (
                            <Alert key={index} variant="danger">{error}</Alert>
                        ))}

                        <Form.Group controlId="formName" className="entity-field">
                            <Form.Label className="entity-label">Название</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formDescription" className="entity-field entity-field--full">
                            <Form.Label className="entity-label">Описание</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formPhoto" className="entity-field">
                            <Form.Label className="entity-label">Фото</Form.Label>
                            <Form.Control
                                type="file"
                                name="photo"
                                onChange={handleFileChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formProject" className="entity-field">
                            <Form.Label className="entity-label">Проект</Form.Label>
                            <div className="entity-select-group">
                                <div className="entity-select-grow">
                                <Select
                                    name="project"
                                    options={projects}
                                    value={projects.find(proj => proj.value === formData.project)}
                                    onChange={(option) => handleFilterChange(option, { name: 'project' })}
                                    classNamePrefix="entity-select"
                                    isClearable
                                />
                                </div>
                                <Button className="entity-add-btn" type="button" onClick={() => setShowProjectModal(true)}>+</Button>
                            </div>
                        </Form.Group>

                        <Form.Group controlId="formUpgrade" className="entity-field">
                            <Form.Label className="entity-label">Апгрейд</Form.Label>
                            <div className="entity-select-group">
                                <div className="entity-select-grow">
                                <Select
                                    name="upgrade"
                                    options={upgrades}
                                    value={upgrades.find(upg => upg.value === formData.upgrade)}
                                    onChange={(option) => handleFilterChange(option, { name: 'upgrade' })}
                                    classNamePrefix="entity-select"
                                    isClearable
                                />
                                </div>
                                <Button className="entity-add-btn" type="button" onClick={() => setShowUpgradeModal(true)}>+</Button>
                            </div>
                        </Form.Group>

                        <Form.Group controlId="formStorage" className="entity-field">
                            <Form.Label className="entity-label">Место хранения</Form.Label>
                            <div className="entity-select-group">
                                <div className="entity-select-grow">
                                <Select
                                    name="storage_location"
                                    options={storages}
                                    value={storages.find(stor => stor.value === formData.storage_location)}
                                    onChange={(option) => handleFilterChange(option, { name: 'storage_location' })}
                                    classNamePrefix="entity-select"
                                    isClearable
                                    required
                                />
                                </div>
                                <Button className="entity-add-btn" type="button" onClick={() => setShowStorageCategoryModal(true)}>+</Button>
                            </div>
                        </Form.Group>

                        <Form.Group controlId="formCompatibility" className="entity-field entity-field--full">
                            <Form.Label className="entity-label">Совместимость</Form.Label>
                            <div className="entity-select-group">
                                <div className="entity-select-grow">
                                <Select
                                    name="compatibility"
                                    options={compatibilities}
                                    value={compatibilities.filter(comp => formData.compatibility.includes(comp.value))}
                                    onChange={(options) => handleFilterChange(options, { name: 'compatibility' })}
                                    classNamePrefix="entity-select"
                                    isMulti
                                    isClearable
                                    required
                                />
                                </div>
                                <Button className="entity-add-btn" type="button" onClick={() => setShowCompatibilityModal(true)}>+</Button>
                            </div>
                        </Form.Group>

                        <Form.Group controlId="formQuantityInUpgrade" className="entity-field">
                            <Form.Label className="entity-label">Количество в апгрейде</Form.Label>
                            <Form.Control
                                type="number"
                                name="quantity_in_upgrade"
                                value={formData.quantity_in_upgrade}
                                onChange={handleInputChange}
                                required
                                min="0"
                            />
                        </Form.Group>

                        <Form.Group controlId="formBatchNumber" className="entity-field">
                            <Form.Label className="entity-label">Партийный номер</Form.Label>
                            <Form.Control
                                type="text"
                                name="batch_number"
                                value={formData.batch_number}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="formQuantity" className="entity-field">
                            <Form.Label className="entity-label">Количество на складе</Form.Label>
                            <Form.Control
                                type="number"
                                name="quantity"
                                value={formData.quantity}
                                onChange={handleInputChange}
                                required
                                min="0"
                            />
                        </Form.Group>

                        <div className="entity-actions">
                            <Button
                                className="entity-btn entity-btn-secondary"
                                type="button"
                                onClick={handleCloneClick}
                            >
                                Клонировать компонент
                            </Button>
                            <Button className="entity-btn entity-btn-primary" type="submit">
                                Сохранить
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Модальные окна для добавления новых сущностей */}
            <ProjectModal
                show={showProjectModal}
                handleClose={() => setShowProjectModal(false)}
                onSubmit={(newProject) => {
                    setProjects(prevProjects => [...prevProjects, { value: newProject.id, label: newProject.name }]);
                    setFormData(prevData => ({
                        ...prevData,
                        project: newProject.id, // Прямо добавляем ID нового проекта
                    }));
                    fetchFilteredData({ ...formData, project: newProject.id });
                    setShowProjectModal(false);
                }}
            />

            <UpgradeModal
                show={showUpgradeModal}
                handleClose={() => setShowUpgradeModal(false)}
                onSubmit={() => {
                    fetchFilteredData(formData);
                    setShowUpgradeModal(false);
                }}
            />
            <StorageCategoryModal
                show={showStorageCategoryModal}
                handleClose={() => setShowStorageCategoryModal(false)}
                onSubmit={() => {
                    fetchFilteredData(formData);
                    setShowStorageCategoryModal(false);
                }}
            />
            <CompatibilityModal
                show={showCompatibilityModal}
                handleClose={() => setShowCompatibilityModal(false)}
                onSubmit={(newCompatibility) => {
                    const option = {
                        value: newCompatibility.id,
                        label: newCompatibility.name
                    };

                    setCompatibilities((prev) => {
                        if (prev.some((item) => item.value === option.value)) {
                            return prev;
                        }
                        return [...prev, option];
                    });

                    setFormData((prevData) => ({
                        ...prevData,
                        compatibility: prevData.compatibility.includes(option.value)
                            ? prevData.compatibility
                            : [...prevData.compatibility, option.value]
                    }));

                    setShowCompatibilityModal(false);
                }}
            />
        </>
    );
}

export default ComponentModal;
